import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  DataList,
  DataListItem,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Heading,
  Link,
  Progress,
  Select,
  Spinner,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import {
  FileProgressItem,
  FileProgressList,
} from '@/components/file-progress-list';
import * as validationPreferences from '@/utils/validationPreferences';
import { MetaValidationMode, MetaValidationProgress } from '../types';

interface ValidationQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  progress: MetaValidationProgress;
  pendingTotalFiles: number;
  metaValidationMode?: MetaValidationMode;
  onStart: () => void;
  onRevalidate: () => void;
  onCancel: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onRetryFailed: () => void;
  onRefreshWhitelist?: () => void;
}

const formatCount = (n: number, singular: string, plural: string): string => {
  return `${n} ${n === 1 ? singular : plural}`;
};

export const ValidationQueueModal: React.FC<ValidationQueueModalProps> = ({
  isOpen,
  onClose,
  progress,
  pendingTotalFiles,
  metaValidationMode = 'server',
  onStart,
  onRevalidate,
  onCancel,
  onPause,
  onResume,
  onRetryFailed,
  onRefreshWhitelist,
}) => {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [batchQueueExpanded, setBatchQueueExpanded] = useState(false);
  const currentBatchSize = validationPreferences.getValidationBatchSize();
  const effectiveTotalFiles =
    progress.totalFiles > 0 ? progress.totalFiles : pendingTotalFiles;
  const effectiveTotalBatches =
    progress.totalBatches > 0
      ? progress.totalBatches
      : effectiveTotalFiles > 0
        ? Math.max(
            1,
            Math.ceil(effectiveTotalFiles / Math.max(1, currentBatchSize))
          )
        : 0;

  const isCompleted =
    !progress.isRunning &&
    effectiveTotalBatches > 0 &&
    progress.completedBatches === effectiveTotalBatches;
  const hasErrors = progress.failedBatches > 0;
  const canRetry = !progress.isRunning && hasErrors;
  const canStart =
    !progress.isRunning &&
    effectiveTotalFiles > 0 &&
    progress.completedBatches === 0;
  const canRevalidate =
    !progress.isRunning && isCompleted && effectiveTotalFiles > 0;
  const canRefreshWhitelist =
    metaValidationMode === 'client' &&
    !progress.isRunning &&
    effectiveTotalFiles > 0;
  const canPause =
    metaValidationMode === 'client' &&
    progress.isRunning &&
    typeof onPause === 'function' &&
    typeof onResume === 'function';

  const headerIcon = useMemo(() => {
    if (progress.isRunning && progress.isPaused) {
      return <IconPlayerPause style={{ color: 'var(--orange-9)' }} />;
    }
    if (progress.isRunning) return <Spinner size="3" className="spinner" />;
    if (!effectiveTotalBatches)
      return <IconPlayerPlay style={{ color: 'var(--blue-9)' }} />;
    if (isCompleted && hasErrors)
      return <IconAlertTriangle style={{ color: 'var(--orange-9)' }} />;
    if (isCompleted && !hasErrors)
      return <IconCheck style={{ color: 'var(--green-9)' }} />;
    return <IconPlayerPlay style={{ color: 'var(--blue-9)' }} />;
  }, [
    effectiveTotalBatches,
    hasErrors,
    isCompleted,
    progress.isPaused,
    progress.isRunning,
  ]);

  const title = useMemo(() => {
    if (progress.isRunning && progress.isPaused) return 'Validation Paused';
    if (progress.isRunning) return 'Validating Files';
    if (isCompleted)
      return hasErrors
        ? 'Validation Completed with Errors'
        : 'Validation Completed Successfully';
    return 'Validate Files';
  }, [hasErrors, isCompleted, progress.isPaused, progress.isRunning]);

  const subtitle = useMemo(() => {
    if (progress.isRunning && progress.isPaused) {
      return `${progress.completedFiles}/${effectiveTotalFiles} files validated • Paused`;
    }
    if (progress.isRunning) {
      return `${progress.completedFiles}/${effectiveTotalFiles} files validated • ${formatCount(progress.remainingFiles, 'file', 'files')} remaining`;
    }
    if (isCompleted) {
      return hasErrors
        ? `${formatCount(progress.failedBatches, 'batch', 'batches')} failed`
        : `All ${formatCount(effectiveTotalFiles, 'file', 'files')} validated`;
    }
    return 'Validation will run in batches. You can cancel or retry failed batches.';
  }, [
    effectiveTotalFiles,
    hasErrors,
    isCompleted,
    progress.completedFiles,
    progress.failedBatches,
    progress.isRunning,
    progress.isPaused,
    progress.remainingFiles,
  ]);

  const batchItems: FileProgressItem[] = useMemo(() => {
    return progress.batches.map(b => {
      const status: FileProgressItem['status'] = (() => {
        switch (b.status) {
          case 'processing':
            return 'processing';
          case 'completed':
            return 'completed';
          case 'error':
            return 'error';
          case 'cancelled':
            return 'cancelled';
          case 'pending':
          default:
            return 'pending';
        }
      })();

      const name = `Batch ${b.index + 1} (${b.fileCount} files)`;
      const progressValue =
        b.status === 'completed' ||
        b.status === 'error' ||
        b.status === 'cancelled'
          ? 100
          : b.status === 'processing'
            ? 50
            : 0;

      return {
        id: b.id,
        name,
        status,
        progress: progressValue,
        error: b.error,
      };
    });
  }, [progress.batches]);

  const currentProcessingIndex = useMemo(() => {
    const idx = progress.batches.findIndex(b => b.status === 'processing');
    return idx >= 0 ? idx : undefined;
  }, [progress.batches]);

  const settingsDisabled = progress.isRunning;

  const currentMaxConcurrent =
    validationPreferences.getValidationMaxConcurrent();
  const currentLaunchIntervalMs =
    validationPreferences.getValidationLaunchIntervalMs?.() ??
    validationPreferences.VALIDATION_LAUNCH_INTERVAL_MS_DEFAULT ??
    250;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open: boolean) => {
        // Prevent closing while running to avoid confusing state.
        if (!open && !progress.isRunning) onClose();
      }}
    >
      <Dialog.Content className="sm:max-w-[560px]">
        <DialogHeader
          startSlot={
            <Avatar radius="medium" size="medium">
              {headerIcon}
            </Avatar>
          }
          title={title}
          subtitle={subtitle}
        />

        <DialogBody>
          <Flex direction="column" gap="3">
            <Card variant="classic" size="small" background={'var(--gray-2)'}>
              <Heading size="2xsmall" weight="medium" mb="2">
                Validation Progress
              </Heading>

              <DataList
                size="medium"
                orientation="horizontal"
                alignValues="end"
              >
                <DataListItem
                  label="Files:"
                  value={formatCount(effectiveTotalFiles, 'file', 'files')}
                />
                <DataListItem
                  label="Validated:"
                  value={formatCount(progress.completedFiles, 'file', 'files')}
                />
                <VisuallyHidden>
                  <DataListItem
                    label="Batches:"
                    value={`${progress.completedBatches}/${effectiveTotalBatches}`}
                  />
                </VisuallyHidden>
                <VisuallyHidden>
                  <DataListItem
                    label="Failed:"
                    value={formatCount(
                      progress.failedBatches,
                      'batch',
                      'batches'
                    )}
                  />
                </VisuallyHidden>
              </DataList>

              <Card
                variant="classic"
                size="xsmall"
                color={
                  progress.isRunning
                    ? 'info'
                    : hasErrors
                      ? 'warning'
                      : 'success'
                }
                mt="4"
              >
                <Flex direction="column" gap="2" style={{ gridColumn: '1/-1' }}>
                  <Flex justify="between" align="center">
                    <Text size="3xsmall">
                      Overall Progress
                      <Text as="span" size="inherit" weight="medium" ml="2">
                        {progress.percent}%
                      </Text>
                    </Text>
                    <Text size="3xsmall">
                      {progress.completedFiles}/{effectiveTotalFiles} files
                    </Text>
                  </Flex>
                  <Progress size="small" value={progress.percent} />
                </Flex>
              </Card>
            </Card>

            <Card variant="classic" size="small" background={'var(--gray-2)'}>
              <Flex justify="between" align="center" mb="2">
                <Heading size="2xsmall" weight="medium">
                  Results
                </Heading>
                {progress.totalBatches > 0 && (
                  <Link
                    href="#"
                    size="1"
                    color="gray"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setBatchQueueExpanded(prev => !prev);
                    }}
                  >
                    {batchQueueExpanded ? (
                      <Flex align="center" gap="1">
                        Hide batch details <IconChevronUp size={14} />
                      </Flex>
                    ) : (
                      <Flex align="center" gap="1">
                        Show batch details <IconChevronDown size={14} />
                      </Flex>
                    )}
                  </Link>
                )}
              </Flex>

              <Flex gap="3" wrap="wrap">
                <Card
                  variant="classic"
                  size="small"
                  color={progress.isRunning ? 'info' : 'gray'}
                  style={{ flex: 1, minWidth: 160 }}
                >
                  <Text size="3xsmall" color="secondary">
                    Validated
                  </Text>
                  <Heading size="small" weight="bold">
                    {progress.completedFiles.toLocaleString()}
                  </Heading>
                  <Text size="3xsmall" color="secondary">
                    of {effectiveTotalFiles.toLocaleString()}
                  </Text>
                </Card>

                <Card
                  variant="classic"
                  size="small"
                  color="success"
                  style={{ flex: 1, minWidth: 160 }}
                >
                  <Text size="3xsmall" color="secondary">
                    Allowed
                  </Text>
                  <Heading size="small" weight="bold">
                    {(progress.allowedCount ?? 0).toLocaleString()}
                  </Heading>
                </Card>

                <Card
                  variant="classic"
                  size="small"
                  color="error"
                  style={{ flex: 1, minWidth: 160 }}
                >
                  <Text size="3xsmall" color="secondary">
                    Not allowed
                  </Text>
                  <Heading 
                    size="small" 
                    weight="bold"
                    style={{
                      color: (progress.notAllowedCount ?? 0) !== 0 
                        ? 'var(--red-9)' 
                        : undefined
                    }}
                  >
                    {(progress.notAllowedCount ?? 0).toLocaleString()}
                  </Heading>
                </Card>
              </Flex>
            </Card>

            {progress.totalBatches > 0 && batchQueueExpanded && (
              <Box>
                <Flex justify="between" align="center" mb="2">
                  <Text size="2xsmall" weight="medium">
                    Batch Queue
                  </Text>
                  <Text size="3xsmall" color="secondary">
                    {formatCount(progress.runningBatches, 'batch', 'batches')}{' '}
                    running •{' '}
                    {formatCount(
                      Math.max(
                        0,
                        progress.totalBatches - progress.completedBatches
                      ),
                      'batch',
                      'batches'
                    )}{' '}
                    remaining
                  </Text>
                </Flex>
                <FileProgressList
                  files={batchItems}
                  currentProcessingIndex={currentProcessingIndex}
                  isProcessing={progress.isRunning}
                  maxHeight="260px"
                />
              </Box>
            )}

            <VisuallyHidden>
              {!settingsExpanded ? (
                <Flex justify="end">
                  <Link
                    href="#"
                    size="1"
                    color="gray"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setSettingsExpanded(true);
                    }}
                  >
                    Validation settings
                  </Link>
                </Flex>
              ) : (
                <Card variant="classic" size="small" background={'var(--gray-2)'}>
                  <Flex justify="between" align="center" mb="2">
                    <Heading size="2xsmall" weight="medium">
                      Validation Settings
                    </Heading>
                    <Link
                      href="#"
                      size="1"
                      color="gray"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        setSettingsExpanded(false);
                      }}
                    >
                      Hide
                    </Link>
                  </Flex>

                  <Flex direction="column" gap="3">
                    <Flex justify="between" align="center">
                      <div>
                        <Text size="2xsmall" weight="medium">
                          Batches at a time
                        </Text>
                        <Text size="3xsmall" color="secondary">
                          How many validation requests can run concurrently.
                        </Text>
                      </div>
                      <div style={{ minWidth: 140 }}>
                        <Select
                          value={String(currentMaxConcurrent)}
                          disabled={settingsDisabled}
                          onValueChange={(value: string) => {
                            const next = Number.parseInt(value, 10);
                            validationPreferences.setValidationMaxConcurrent(
                              next
                            );
                          }}
                          options={[
                            { value: '1', label: '1' },
                            { value: '2', label: '2' },
                            { value: '3', label: '3' },
                            { value: '4', label: '4' },
                            { value: '5', label: '5' },
                            { value: '8', label: '8' },
                            { value: '10', label: '10' },
                          ].filter(opt => {
                            const v = Number.parseInt(opt.value, 10);
                            return (
                              v >=
                                (validationPreferences.VALIDATION_MAX_CONCURRENT_MIN ??
                                  1) &&
                              v <=
                                (validationPreferences.VALIDATION_MAX_CONCURRENT_MAX ??
                                  10)
                            );
                          })}
                        />
                      </div>
                    </Flex>

                    <Flex justify="between" align="center">
                      <div>
                        <Text size="2xsmall" weight="medium">
                          Batch size
                        </Text>
                        <Text size="3xsmall" color="secondary">
                          How many files to validate per request.
                        </Text>
                      </div>
                      <div style={{ minWidth: 140 }}>
                        <Select
                          value={String(currentBatchSize)}
                          disabled={settingsDisabled}
                          onValueChange={(value: string) => {
                            const next = Number.parseInt(value, 10);
                            validationPreferences.setValidationBatchSize(next);
                          }}
                          options={(
                            (validationPreferences.VALIDATION_BATCH_SIZE_OPTIONS as readonly number[]) ?? [
                              1000, 5000, 10000,
                            ]
                          ).map(v => ({
                            value: String(v),
                            label: v.toLocaleString(),
                          }))}
                        />
                      </div>
                    </Flex>

                    <Flex justify="between" align="center">
                      <div>
                        <Text size="2xsmall" weight="medium">
                          Start interval
                        </Text>
                        <Text size="3xsmall" color="secondary">
                          Delay between starting batches (helps reduce bursts).
                        </Text>
                      </div>
                      <div style={{ minWidth: 140 }}>
                        <Select
                          value={String(currentLaunchIntervalMs)}
                          disabled={settingsDisabled}
                          onValueChange={(value: string) => {
                            const next = Number.parseInt(value, 10);
                            validationPreferences.setValidationLaunchIntervalMs?.(
                              next
                            );
                          }}
                          options={(
                            (validationPreferences.VALIDATION_LAUNCH_INTERVAL_MS_OPTIONS as readonly number[]) ?? [
                              0, 100, 250, 500, 1000,
                            ]
                          ).map(v => ({
                            value: String(v),
                            label: v === 0 ? 'No delay' : `${v} ms`,
                          }))}
                        />
                      </div>
                    </Flex>

                    {settingsDisabled && (
                      <Text size="3xsmall" color="secondary">
                        Settings are locked while validation is running.
                      </Text>
                    )}
                  </Flex>
                </Card>
              )}
            </VisuallyHidden>

            {progress.lastRunWasCancelled && !progress.isRunning && (
              <Callout
                icon={<IconX />}
                title="Cancelled"
                variant="surface"
                color="warning"
              >
                Validation was cancelled. You can start again or retry failed
                batches.
              </Callout>
            )}

            {isCompleted && hasErrors && (
              <Callout
                icon={<IconAlertTriangle />}
                title="Validation Completed with Errors"
                variant="surface"
                color="warning"
              >
                Some batches failed. You can retry failed batches.
              </Callout>
            )}

            {isCompleted && !hasErrors && (
              <Callout
                icon={<IconCheck />}
                title="Validation Successful"
                variant="surface"
                color="success"
              >
                All batches completed successfully.
              </Callout>
            )}
          </Flex>
        </DialogBody>

        <DialogFooter>
          {canStart && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={progress.isRunning ? onCancel : onClose}
            >
              {progress.isRunning ? 'Cancel Validation' : 'Close'}
            </Button>
          )}

          {canRevalidate && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onRevalidate}
              startIcon={<IconRefresh />}
            >
              Revalidate
            </Button>
          )}

          {progress.isRunning && canPause && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onCancel}
            >
              Cancel Validation
            </Button>
          )}

          {canPause && (
            <Button
              variant="classic"
              color={progress.isPaused ? 'primary' : 'secondary'}
              onClick={progress.isPaused ? onResume : onPause}
              startIcon={
                progress.isPaused ? <IconPlayerPlay /> : <IconPlayerPause />
              }
            >
              {progress.isPaused ? 'Resume' : 'Pause'}
            </Button>
          )}

          {canRefreshWhitelist && onRefreshWhitelist && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onRefreshWhitelist}
              startIcon={<IconRefresh />}
            >
              Refresh Whitelist
            </Button>
          )}

          {canRetry && (
            <Button
              variant="classic"
              color="warning"
              onClick={onRetryFailed}
              startIcon={<IconRefresh />}
            >
              Retry Failed Batches ({progress.failedBatches})
            </Button>
          )}

          {canStart && (
            <Button
              variant="classic"
              color="primary"
              onClick={onStart}
              startIcon={<IconPlayerPlay />}
            >
              Start Validation
            </Button>
          )}

          {!canStart && !canPause && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={progress.isRunning ? onCancel : onClose}
            >
              {progress.isRunning ? 'Cancel Validation' : 'Close'}
            </Button>
          )}
        </DialogFooter>
      </Dialog.Content>
    </Dialog.Root>
  );
};
